<script setup lang="ts">
defineOptions({ inheritAttrs: false });

// teleport=false : à utiliser quand la sheet vit dans une modale Reka (UModal). Teleportée
// vers body, elle hériterait du pointer-events:none posé par le dialog modal (clics traversants),
// un clic dessus serait un « pointer down outside » qui fermerait la modale, et le focus trap la
// rendrait inaccessible au clavier. La modale hôte le fournit à tout son sous-arbre via
// provide(bottomSheetTeleportKey, false) ; la prop garde la priorité si passée explicitement.
// Défaut `undefined` (pas `false`) : un booléen absent serait casté à false et court-circuiterait `?? injectedTeleport`.
const props = withDefaults(defineProps<{ teleport?: boolean }>(), { teleport: undefined });
const injectedTeleport = inject(bottomSheetTeleportKey, true);
const shouldTeleport = computed(() => props.teleport ?? injectedTeleport);

const open = defineModel<boolean>("open", { default: false });

const { t } = useI18n();

const sheetEl = useTemplateRef<HTMLElement>("sheetEl");
const sentinelEl = useTemplateRef<HTMLElement>("sentinelEl");
const headerEl = useTemplateRef<HTMLElement>("headerEl");
const contentEl = useTemplateRef<HTMLElement>("contentEl");
const titleId = useId();
const isStuck = ref(false);
let previousFocus: HTMLElement | null = null;

useAppEscapeKey(
  () => {
    open.value = false;
  },
  { when: open, priority: 200 }
);

useAppScrollLock(open);

const CLOSE_NEAR_BOTTOM_PX = 80;

watchEffect(
  (onCleanup) => {
    if (!open.value || !sentinelEl.value || !sheetEl.value || !contentEl.value) {
      isStuck.value = false;
      return;
    }
    const target = sheetEl.value;
    const stickyObs = new IntersectionObserver(
      ([entry]) => {
        if (entry) isStuck.value = !entry.isIntersecting;
      },
      { root: target, threshold: 0 }
    );
    stickyObs.observe(sentinelEl.value);

    let hasRevealed = false;
    const closeObs = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          hasRevealed = true;
        } else if (hasRevealed && userScrolled && !isProgrammaticScroll && entry.rootBounds && entry.boundingClientRect.top >= entry.rootBounds.bottom) {
          open.value = false;
        }
      },
      { root: target, rootMargin: `0px 0px -${CLOSE_NEAR_BOTTOM_PX}px 0px`, threshold: 0 }
    );
    closeObs.observe(contentEl.value);

    onCleanup(() => {
      stickyObs.disconnect();
      closeObs.disconnect();
    });
  },
  { flush: "post" }
);

function getFocusable(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter((el) => el.offsetParent !== null);
}

let userScrolled = false;
let isProgrammaticScroll = false;
let programmaticScrollTimer: ReturnType<typeof setTimeout> | undefined;

function bumpProgrammaticWindow() {
  isProgrammaticScroll = true;
  if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer);
  programmaticScrollTimer = setTimeout(() => {
    isProgrammaticScroll = false;
    programmaticScrollTimer = undefined;
  }, 800);
}

function recenter() {
  if (!headerEl.value || !sheetEl.value || !contentEl.value) return;
  bumpProgrammaticWindow();
  requestAnimationFrame(() => {
    const scroller = sheetEl.value;
    const header = headerEl.value;
    const content = contentEl.value;
    if (!scroller || !header || !content) return;
    const sheetInnerHeight = header.offsetHeight + content.offsetHeight;
    if (sheetInnerHeight < scroller.clientHeight / 2) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: "auto" });
    } else {
      header.scrollIntoView({ block: "center", behavior: "auto" });
    }
  });
}

watchEffect((onCleanup) => {
  if (!open.value || !sheetEl.value || !contentEl.value) return;
  const scroller = sheetEl.value;
  const onScroll = () => {
    if (isProgrammaticScroll) return;
    userScrolled = true;
  };
  scroller.addEventListener("scroll", onScroll, { passive: true });
  const ro = new ResizeObserver(() => {
    bumpProgrammaticWindow();
    if (!userScrolled) recenter();
  });
  ro.observe(contentEl.value);
  onCleanup(() => {
    scroller.removeEventListener("scroll", onScroll);
    ro.disconnect();
  });
});

watch(
  open,
  async (v) => {
    if (typeof document === "undefined") return;
    if (v) {
      previousFocus = (document.activeElement as HTMLElement) ?? null;
      userScrolled = false;
      await nextTick();
      recenter();
      getFocusable(sheetEl.value)[0]?.focus({ preventScroll: true });
    } else {
      previousFocus?.focus?.();
      previousFocus = null;
    }
  },
  { immediate: true }
);

function onKeydown(e: KeyboardEvent) {
  if (e.key !== "Tab" || !sheetEl.value) return;
  const focusable = getFocusable(sheetEl.value);
  if (!focusable.length) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (e.shiftKey && document.activeElement === first) {
    last.focus();
    e.preventDefault();
  } else if (!e.shiftKey && document.activeElement === last) {
    first.focus();
    e.preventDefault();
  }
}

defineExpose({ scrollEl: sheetEl });
</script>

<template>
  <Teleport to="body" :disabled="!shouldTeleport">
    <Transition name="sheet">
      <div
        v-if="open"
        ref="sheetEl"
        v-bind="$attrs"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        class="fixed inset-x-0 top-0 h-dvh z-60 overflow-y-auto scroll-smooth overscroll-contain snap-y snap-proximity pointer-events-auto"
        @keydown="onKeydown"
      >
        <div aria-hidden="true" @click="open = false" class="snap-center h-dvh" />

        <div class="group/sheet flex max-h-dvh flex-col bg-white rounded-t-2xl shadow-2xl pb-safe">
          <div ref="sentinelEl" aria-hidden="true" class="h-0" />
          <header
            ref="headerEl"
            :class="isStuck ? 'rounded-t-none' : 'rounded-t-2xl'"
            class="snap-center shrink-0 sticky top-0 flex items-start gap-2 p-4 z-1 transition-[border-radius] border-b rounded-t-2xl bg-(--header-bg,var(--color-white)) border-(--header-border,var(--color-lagoon-300))"
          >
            <div :id="titleId" class="grow"><slot name="header" /></div>
            <UButton class="-m-1" icon="icon:close" variant="ghost" color="neutral" :aria-label="t('ui.close')" @click="() => { open = false }" />
          </header>
          <div ref="contentEl" class="snap-start min-h-0 overflow-y-auto overscroll-contain">
            <slot />
          </div>
          <div aria-hidden="true" class="snap-end" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sheet-enter-active,
.sheet-leave-active {
  transition: transform 300ms ease-out;
}
.sheet-enter-from,
.sheet-leave-to {
  transform: translateY(100%);
}
</style>
